<template>
  <div :class="[{ 'with-image': html.includes('<img'), 'deck-terms': deckTerms }, `flashcard-${type}-content`, place]" v-html="html" />
</template>

<script>
export default {
  props: {
    type: { // question/answer
      type: String,
      required: true,
    },
    html: { // html content
      type: String,
      required: true,
    },
    place: { // where is used
      type: String,
      required: false,
      default: 'exam',
    },
    deckTerms: Boolean,
  },
  beforeUpdate() {
    this.$nextTick(() => {
      this.loadGallery();
    });
  },
  mounted() {
    Array.from(document.getElementsByClassName('text-content')).forEach((el) => {
      if (['&nbsp;', '<span></span>'].includes(el.innerHTML)) {
        el.remove();
      }
    });
    this.$nextTick(() => {
      this.loadGallery();

      if (window.MathJax) {
        window.MathJax.Hub.Config({
          tex2jax: {
            inlineMath: [['$', '$'], ['\\(', '\\)']],
            processEscapes: true,
          },
          'HTML-CSS': {
            linebreaks: { automatic: true, width: 'container' },
          },
          SVG: {
            linebreaks: { width: '10000px' },
          },
          jax: ['input/TeX', 'output/SVG'],
        });
        window.MathJax.Hub.Queue(['Typeset', window.MathJax.Hub]);
      }
    });
  },
  methods: {
    loadGallery() {
      document.querySelectorAll('p').forEach((p) => {
        if ((p.textContent.trim() === '' && !p.querySelector('.image-lightbox')) || p.textContent === '&nbsp;') {
          // eslint-disable-next-line no-param-reassign
          p.style.display = 'none';
        }
      });
      this.$el.querySelectorAll('.image-lightbox').forEach((gallery) => {
        const image = gallery.querySelector('img');
        const { src, dataset } = image;
        const srcMaxSize = src.replace(/resources\/blog\/thumb@[0-9]{2,4}_/, 'resources/blog/').replace(/webp$/, dataset.ext);
        gallery.addEventListener('click', (e) => {
          e.preventDefault();
          this.$store.dispatch('modals/open', {
            name: 'gallery',
            images: [srcMaxSize],
            activeIdx: 1,
          });
        });
      });
    },
  },
};
</script>

<style lang="scss">
.flashcard-question-content,
.flashcard-answer-content {
  width: 100%;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;

  .text-content {
    margin: 0;

    p {
      margin: 0;
    }

    .inline {
      display: inline-block;
    }

    .block .MJXc-display {
      margin: 0;
    }
  }

  p:empty {
    display: none;
  }

  &.with-image {
    .text-content > *:not(.mathjax-wrapper),
    & > * {
      font-size: 21px !important;
    }

    .text-content {
      width: calc(50% - 24px);
      text-align: left;
    }

    .lightbox-wrapper {
      // only image
      display: flex;
      align-items: center;
      justify-content: center;
      width: auto;
      height: 250px;

      .image-lightbox {
        border: 0;
        height: 100%;
        width: auto;
        margin: 0 auto;
        display: flex;
        align-items: center;
        justify-content: center;

        img {
          width: auto !important;
          object-fit: contain;
          overflow: hidden;
        }
      }
    }

    .text-content ~ .lightbox-wrapper {
      // text + image
      width: 50%;
      margin-left: var(--spacing-30);

      .image-lightbox {
        height: auto;
      }
    }

    &.deck-terms {
      .text-content ~ .lightbox-wrapper {
        width: 100% !important;
        height: auto;
        min-height: 70px;
        margin-left: 0;

        .image-lightbox {
          height: 100%;

          img {
            max-height: 170px !important;
          }
        }
      }

      .lightbox-wrapper {
        .image-lightbox img {
          height: 100% !important;
        }
      }
    }
  }

  &.deck-terms {
    padding-bottom: 0;
  }

  &:not(.with-image) {
    height: 100%;
  }
}

.flashcard-answer-content {
  .text-content > *:not(.mathjax-wrapper),
  & > * {
    margin: 0;
    line-height: 110% !important;
    color: var(--main-dark) !important;
    letter-spacing: 0.32px !important;
    background: none !important;
    border: none !important;
  }

  &.exam,
  &.exam-results {
    &.with-image {
      .text-content {
        width: 100%;

        > * {
          font-size: 16px !important;
        }
      }
    }

    .lightbox-wrapper {
      display: none;
    }
  }

  &.exam {
    justify-content: flex-start;
    cursor: pointer;

    .text-content > *:not(.mathjax-wrapper),
    & > * {
      text-align: left;
      margin: 0 !important;
      font-size: 16px !important;
      color: var(--main-dark) !important;
    }

    img {
      display: none;
    }
  }

  &.study {
    height: 100%;

    .text-content > *:not(.mathjax-wrapper),
    & > * {
      text-align: center;
      font-size: 24px !important;
    }

    &.with-image .lightbox-wrapper .image-lightbox img {
      height: 100% !important;
    }

    &.less-height .lightbox-wrapper {
      height: 260px;
    }

    &.with-image .text-content > *:not(.mathjax-wrapper),
    & > * {
      font-size: 21px !important;
    }
  }

  &.exam-results {
    justify-content: flex-start;

    .text-content {
      text-align: left !important;

      *:not(.mathjax-wrapper) {
        line-height: 27px !important;
      }
    }
  }

  &.terms {
    &.with-image {
      flex-direction: column;
      height: 100%;

      .text-content {
        width: 100%;
        margin-bottom: var(--spacing-12);

        > *:not(.mathjax-wrapper) {
          font-size: 21px !important;
        }
      }
    }

    .text-content > *:not(.mathjax-wrapper),
    & > * {
      font-size: 24px !important;
    }
  }
}

.flashcard-question-content {
  .text-content > *:not(.mathjax-wrapper),
  & > * {
    margin: 0;
    font-size: 24px !important;
    line-height: 110% !important;
    color: var(--main-dark) !important;
    background: none !important;
  }

  &.study {
    height: 100%;

    &.with-image .lightbox-wrapper .image-lightbox img {
      height: 100% !important;
    }

    &.less-height .lightbox-wrapper {
      height: 260px;
    }
  }

  &.exam {
    height: calc(100% - 40px);

    .text-content ~ .lightbox-wrapper {
      .image-lightbox {
        height: 100%;

        img {
          width: 100% !important;
          height: 100% !important;
          object-fit: cover;
        }
      }
    }

    .lightbox-wrapper {
      height: 250px;
    }
  }

  &.terms {
    &.with-image {
      flex-direction: column;
      gap: unset;
      height: 100%;

      .text-content {
        width: 100%;
        margin-bottom: var(--spacing-12);

        *:not(.mathjax-wrapper) {
          font-size: 21px !important;
        }
      }
    }
  }
}

@include bp($bp-laptop) {
  .flashcard-question-content,
  .flashcard-answer-content {
    &.with-image {
      .text-content > *:not(.mathjax-wrapper),
      & > * {
        font-size: 18px !important;
      }

      .text-content {
        width: 50%;
        text-align: left;
      }

      .lightbox-wrapper {
        // only image
        height: 250px;
      }

      .text-content ~ .lightbox-wrapper {
        // text + image
        height: 260px;
      }

      &.flashcard-answer-content.exam {
        .text-content > :not(.mathjax-wrapper),
        & > * {
          font-size: 16px !important;
        }
      }
    }
  }

  .flashcard-answer-content {
    &.study {
      .text-content > *:not(.mathjax-wrapper),
      & > * {
        font-size: 21px !important;
      }

      &.with-image .text-content > *:not(.mathjax-wrapper),
      & > * {
        font-size: 18px !important;
      }
    }

    &.terms {
      .text-content > *:not(.mathjax-wrapper),
      & > * {
        font-size: 21px !important;
      }

      &.with-image {
        .text-content > *:not(.mathjax-wrapper),
        & > * {
          font-size: 18px !important;
        }
      }
    }
  }

  .flashcard-question-content {
    .text-content > *:not(.mathjax-wrapper),
    & > * {
      font-size: 21px !important;
    }

    &.exam {
      .lightbox-wrapper {
        height: 250px;
      }
    }

    &.terms {
      &.with-image {
        .text-content > *:not(.mathjax-wrapper),
        & > * {
          font-size: 18px !important;
        }

        .text-content *:not(.mathjax-wrapper) {
          font-size: 18px !important;
        }
      }
    }
  }
}

@include bp($bp-mobile) {
  .flashcard-question-content,
  .flashcard-answer-content {
    flex-direction: column;

    &.with-image {
      .text-content {
        width: 100%;
        text-align: center;

        *:not(.mathjax-wrapper) {
          font-size: 16px !important;
        }
      }

      .lightbox-wrapper {
        width: 100%;
        height: 200px;
      }

      .text-content ~ .lightbox-wrapper {
        width: 100%;
        margin-left: 0;

        .image-lightbox {
          height: 100%;
        }
      }

      &.study {
        .text-content {
          margin-bottom: var(--spacing-12);
        }

        .lightbox-wrapper {
          max-height: 420px;

          .image-lightbox img {
            width: 100% !important;
            height: 100% !important;
          }
        }

        .text-content ~ .lightbox-wrapper {
          height: 160px;
        }
      }
    }

    &.study,
    &.exam,
    &.terms {
      .text-content > *:not(.mathjax-wrapper),
      & > * {
        font-size: 16px !important;
      }

      &.with-image .text-content > *:not(.mathjax-wrapper),
      &.with-image .text-content *:not(.mathjax-wrapper),
      & > * {
        font-size: 16px !important;
      }
    }

    &.terms {
      gap: 0;

      &.with-image {
        display: flex;

        .lightbox-wrapper {
          height: auto;

          .image-lightbox {
            height: auto;

            img {
              max-height: 200px;
            }
          }
        }

        .text-content ~ .lightbox-wrapper {
          max-height: 160px;
        }
      }
    }

    &.study {
      gap: 0;
    }

    &.exam-results {
      align-items: flex-start;
    }

    .text-content > *:not(.mathjax-wrapper),
    & > * {
      font-size: 16px !important;
    }
  }

  .flashcard-question-content {
    &.exam {
      .text-content {
        margin-bottom: var(--spacing-16);
      }
    }
  }

  .flashcard-answer-content {
    &.exam,
    &.exam-results {
      flex-direction: unset;
    }
  }
}
</style>
